import {
  ExternalLinkType,
  LanguageSelector,
  Link,
  Navbar as SharedNavbar,
  NAVBAR_ANIMATED_ELEMENT_STYLES,
  NavbarState,
  PageContext,
  PageLinks,
  SocialMedia,
} from '@shared/ui'
import classNames from 'classnames'
import React, { FC, ReactNode, useCallback, useContext } from 'react'
import { ReactSVG } from 'react-svg'

import useSocialMediaLinks from 'fika/hooks/SocialMediaLinks'
import Pages, { getLinks, getSlug, getTheme } from 'fika/pages'

import useData from './query'
import useState from './state'
import styles from './styles.module.css'

const Navbar: FC = () => {
  const { slug } = useContext(PageContext)
  const socialMediaLinks = useSocialMediaLinks()

  const {
    logo: { publicURL: logo },
    logoMobile: { publicURL: logoMobile },
    site: {
      siteMetadata: { externalUrls },
    },
  } = useData()

  const {
    getHomeLinkWidth,
    getLanguageSelectorWidth,
    getPageLinksWidth,
    homeLinkImageRef,
    homeLinkRef,
    languageSelectorRef,
    pageLinkRef,
  } = useState(externalUrls)

  return (
    <SharedNavbar
      getElementsWidth={useCallback(
        () =>
          getHomeLinkWidth() + getPageLinksWidth() + getLanguageSelectorWidth(),
        [getHomeLinkWidth, getPageLinksWidth, getLanguageSelectorWidth]
      )}
      getTheme={getTheme}
      left={useCallback<
        (
          state: NavbarState,
          burgerized: boolean,
          toggleFullscreen: () => void
        ) => ReactNode
      >(
        (state, burgerized, toggleFullscreen) =>
          slug !== getSlug(Pages.HOME, externalUrls) && (
            <Link
              ref={homeLinkRef}
              className={classNames(styles.link, styles.homeLink)}
              to="/"
              onClick={
                burgerized && state === NavbarState.FULLSCREEN
                  ? toggleFullscreen
                  : undefined
              }
            >
              <ReactSVG
                afterInjection={homeLinkImageRef}
                className={styles.homeLinkImage}
                src={burgerized ? logoMobile : logo}
              />
            </Link>
          ),
        [externalUrls, homeLinkImageRef, homeLinkRef, logo, logoMobile, slug]
      )}
      right={useCallback<
        (
          state: NavbarState,
          burgerized: boolean,
          toggleFullscreen: () => void
        ) => ReactNode
      >(
        (state, burgerized, toggleFullscreen) => (
          <>
            {burgerized && (
              <div className={styles.socialMediaLinks}>
                <SocialMedia
                  focusable={state === NavbarState.FULLSCREEN}
                  links={
                    state === NavbarState.FULLSCREEN
                      ? socialMediaLinks.map(link => ({
                          ...link,
                          onClick: toggleFullscreen,
                        }))
                      : socialMediaLinks
                  }
                  type={ExternalLinkType.FABS}
                />
              </div>
            )}

            <PageLinks
              ref={burgerized ? undefined : pageLinkRef}
              className={classNames(
                styles.link,
                NAVBAR_ANIMATED_ELEMENT_STYLES
              )}
              focusable={
                burgerized ? state === NavbarState.FULLSCREEN : undefined
              }
              links={
                burgerized && state === NavbarState.FULLSCREEN
                  ? getLinks(externalUrls).map(link => ({
                      ...link,
                      onClick: toggleFullscreen,
                    }))
                  : getLinks(externalUrls)
              }
            />

            <LanguageSelector
              ref={burgerized ? undefined : languageSelectorRef}
              containerClassName={classNames(
                styles.link,
                NAVBAR_ANIMATED_ELEMENT_STYLES
              )}
              focusable={
                burgerized ? state === NavbarState.FULLSCREEN : undefined
              }
              onClick={
                burgerized && state === NavbarState.FULLSCREEN
                  ? toggleFullscreen
                  : undefined
              }
            />
          </>
        ),
        [externalUrls, languageSelectorRef, pageLinkRef, socialMediaLinks]
      )}
    />
  )
}

export default Navbar
